import React from "react";
import styled, {css} from "styled-components";
import {SiteLink as RouterLink} from "nav/SiteLink";
import {useMessages} from "i18n";
import {fontFamilies, fontWeights} from "theme/tokens";
import {ContentSurface} from "components/ContentPage";
import {GlanceCell, LaneTone, useGlanceRows, useHowItWorksLanes} from "./howItWorks";
import {useFaqItems} from "./faq";

/**
 * The crawlable /features page (ui#97, wave 2) — the in-flow twin of {@link ./HowItWorksDialog}.
 * Both render from the SAME `howItWorksLanes` + `glanceRows` data ({@link ./howItWorks.ts}), so the
 * page and the dialog can never disagree: one copy source, two surfaces. The dialog keeps the
 * animated mini-stages for delight; this page emits plain, snippet-eligible HTML (real headings and
 * a real comparison `<table>`) that crawlers index.
 *
 * Dumb view: pure data → markup, tokens for every color, SSG-safe (no browser APIs, no portals).
 */

const toneColor = (tone: LaneTone) => css`
	${({theme}) => (tone === "blue" ? theme.tokens.blue : theme.tokens.teal)}
`;

const toneTint = (tone: LaneTone) => css`
	${({theme}) => (tone === "blue" ? theme.tokens.accentTint : theme.tokens.signalTint)}
`;

const Title = styled.h1`
	margin: 0 0 10px;
	font-family: ${fontFamilies.displaySerif};
	font-weight: ${fontWeights.display};
	/* Kept identical to the pricing header it has always matched, restored with the display face
	   (ui#1257) from the clamp(28px, 4.2vw, 38px) that face needed. Measured on Chromium: one line
	   down to 375px, where the Romanian title fills 338.31px of a 339px measure. Read that as "it
	   wraps around 375px", not as "it holds at 375px" — 0.69px is inside the noise between text
	   engines, so another platform rounds it onto two lines. That is deliberate rather than a margin
	   to defend: a page title wrapping on a phone is normal here, and holding it would need a size
	   that stops reading as a display heading at all. */
	font-size: clamp(34px, 5vw, 52px);
	line-height: 1.02;
	letter-spacing: -0.015em;
	color: ${({theme}) => theme.tokens.ink};
`;

const Lede = styled.p`
	margin: 0 0 32px;
	max-width: 560px;
	font-size: 16px;
	line-height: 1.6;
	color: ${({theme}) => theme.tokens.ink2};
`;

const Grid = styled.div`
	display: grid;
	grid-template-columns: 1fr 1fr;
	gap: 20px;

	@media (max-width: 820px) {
		grid-template-columns: 1fr;
	}
`;

const LaneCard = styled.section<{$tone: LaneTone}>`
	display: flex;
	flex-direction: column;
	border: 1.5px solid color-mix(in oklch, ${({$tone}) => toneColor($tone)} 32%, ${({theme}) => theme.tokens.cardBorder});
	border-radius: 20px;
	padding: 24px;
	background: ${({theme}) => theme.tokens.card};
`;

const BadgeRow = styled.div`
	display: flex;
	align-items: center;
	gap: 9px;
	margin-bottom: 13px;
	flex-wrap: wrap;
`;

const Badge = styled.span<{$tone: LaneTone}>`
	display: inline-flex;
	align-items: center;
	gap: 7px;
	font-family: ${fontFamilies.mono};
	font-size: 11px;
	font-weight: 700;
	text-transform: uppercase;
	letter-spacing: 0.05em;
	padding: 6px 11px;
	border-radius: 9px;
	color: ${({$tone}) => toneColor($tone)};
	background: ${({$tone}) => toneTint($tone)};
`;

const GhostTag = styled.span`
	font-family: ${fontFamilies.mono};
	font-size: 10px;
	font-weight: 700;
	text-transform: uppercase;
	letter-spacing: 0.05em;
	color: ${({theme}) => theme.tokens.ink3};
	border: 1.5px solid ${({theme}) => theme.tokens.line};
	border-radius: 9px;
	padding: 5px 10px;
`;

const LaneTitle = styled.h2`
	margin: 0 0 8px;
	font-weight: 700;
	font-size: 22px;
	letter-spacing: -0.015em;
	color: ${({theme}) => theme.tokens.ink};
	line-height: 1.15;
`;

const LaneDescription = styled.p`
	margin: 0 0 18px;
	font-size: 15px;
	color: ${({theme}) => theme.tokens.ink2};
	line-height: 1.6;
`;

const BulletList = styled.ul`
	list-style: none;
	margin: 0 0 18px;
	padding: 0;
	display: grid;
	grid-template-columns: 1fr 1fr;
	gap: 11px;

	@media (max-width: 420px) {
		grid-template-columns: 1fr;
	}

	li {
		display: flex;
		gap: 9px;
		font-size: 13.5px;
		color: ${({theme}) => theme.tokens.ink};
		line-height: 1.35;
		align-items: flex-start;
	}
`;

const Check = styled.span<{$tone: LaneTone}>`
	color: ${({$tone}) => toneColor($tone)};
	font-weight: 700;
	flex: none;
`;

const LaneFoot = styled.p`
	margin: auto 0 0;
	display: flex;
	align-items: center;
	gap: 9px;
	padding-top: 15px;
	border-top: 1px solid ${({theme}) => theme.tokens.line};
	font-size: 13.5px;

	span {
		color: ${({theme}) => theme.tokens.ink3};
	}

	b {
		color: ${({theme}) => theme.tokens.ink};
	}
`;

const GlanceHeading = styled.h2`
	text-align: center;
	font-weight: 700;
	font-size: clamp(22px, 3vw, 28px);
	color: ${({theme}) => theme.tokens.ink};
	margin: 48px 0 3px;
	letter-spacing: -0.015em;
`;

const GlanceSub = styled.p`
	text-align: center;
	font-size: 14px;
	color: ${({theme}) => theme.tokens.ink3};
	margin: 0 0 18px;
`;

const GlanceTableWrap = styled.div`
	border: 1.5px solid ${({theme}) => theme.tokens.line};
	border-radius: 16px;
	overflow-x: auto;
`;

const GlanceTable = styled.table`
	width: 100%;
	border-collapse: collapse;
	text-align: left;
	min-width: 520px;

	th,
	td {
		padding: 13px 18px;
		font-size: 13.5px;
		border-top: 1px solid ${({theme}) => theme.tokens.line};
		color: ${({theme}) => theme.tokens.ink2};
	}

	thead th {
		border-top: none;
	}

	/* Steps down so the head's GlanceLane stays the heavier of the pair — see HowItWorksDialog.module.css. */
	td b {
		color: ${({theme}) => theme.tokens.ink};
		font-weight: 600;
	}

	th:first-child,
	td:first-child {
		width: 26%;
		font-family: ${fontFamilies.mono};
		font-size: 10.5px;
		font-weight: 700;
		text-transform: uppercase;
		letter-spacing: 0.05em;
		color: ${({theme}) => theme.tokens.ink3};
	}

	th:nth-child(2),
	td:nth-child(2) {
		background: color-mix(in oklch, ${({theme}) => theme.tokens.blue} 5%, transparent);
	}

	th:nth-child(3),
	td:nth-child(3) {
		background: color-mix(in oklch, ${({theme}) => theme.tokens.teal} 5%, transparent);
	}

	@media (max-width: 640px) {
		th,
		td {
			padding: 11px 11px;
			font-size: 12.5px;
		}
	}
`;

const CheckChip = styled.span<{$tone: LaneTone}>`
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 20px;
	height: 20px;
	border-radius: 50%;
	font-size: 11px;
	font-weight: 700;
	line-height: 1;
	color: ${({$tone}) => toneColor($tone)};
	background: color-mix(in oklch, ${({$tone}) => toneColor($tone)} 14%, transparent);
`;

const GlanceLane = styled.span<{$tone: LaneTone}>`
	display: inline-flex;
	align-items: center;
	gap: 8px;
	font-weight: 700;
	font-size: 14px;
	color: ${({theme}) => theme.tokens.ink};

	&::before {
		content: "";
		width: 8px;
		height: 8px;
		border-radius: 50%;
		background: ${({$tone}) => toneColor($tone)};
	}
`;

const Closing = styled.p`
	margin: 28px 0 0;
	text-align: center;
	font-size: 15px;
	color: ${({theme}) => theme.tokens.ink2};

	a {
		color: ${({theme}) => theme.tokens.blue};
		text-decoration: none;
	}

	a:hover {
		text-decoration: underline;
	}

	a:focus-visible {
		outline: 2px solid ${({theme}) => theme.tokens.focusOutline};
		outline-offset: 2px;
		border-radius: 3px;
	}
`;

/** A left-aligned content section (encryption overview, FAQ) below the how-it-works comparison. */
const Section = styled.section`
	margin-top: 52px;

	@media (max-width: 640px) {
		margin-top: 40px;
	}
`;

const SectionTitle = styled.h2`
	margin: 0 0 12px;
	font-weight: 700;
	font-size: clamp(22px, 3vw, 28px);
	letter-spacing: -0.015em;
	line-height: 1.15;
	color: ${({theme}) => theme.tokens.ink};
`;

const SectionBody = styled.p`
	margin: 0;
	max-width: 760px;
	font-size: 15.5px;
	line-height: 1.65;
	color: ${({theme}) => theme.tokens.ink2};
`;

/** Two-column FAQ grid (single column ≤760px), mirroring the retired home band's layout. */
const FaqGrid = styled.div`
	display: grid;
	gap: 26px;
	margin-top: 22px;

	@media (min-width: 761px) {
		grid-template-columns: 1fr 1fr;
		gap: 26px 40px;
	}
`;

const FaqItemBlock = styled.div``;

const FaqQuestion = styled.h3`
	margin: 0 0 7px;
	font-weight: 700;
	font-size: 16.5px;
	letter-spacing: -0.01em;
	line-height: 1.35;
	color: ${({theme}) => theme.tokens.ink};
`;

const FaqAnswer = styled.p`
	margin: 0;
	font-size: 14.5px;
	line-height: 1.6;
	color: ${({theme}) => theme.tokens.ink2};
`;

export type FeaturesFaqProps = {
	headingId: string;
	headingRef?: React.Ref<HTMLHeadingElement>;
	focusableHeading?: boolean;
};

/** FAQ body shared by the crawlable Features page and its in-place dialog. */
export function FeaturesFaq({headingId, headingRef, focusableHeading = false}: FeaturesFaqProps) {
	const {faq} = useMessages().features;
	const faqItems = useFaqItems();
	return (
		<Section aria-labelledby={headingId}>
			<SectionTitle id={headingId} ref={headingRef} tabIndex={focusableHeading ? -1 : undefined}>
				{faq.title}
			</SectionTitle>
			<FaqGrid>
				{faqItems.map((item) => (
					<FaqItemBlock key={item.question}>
						<FaqQuestion>{item.question}</FaqQuestion>
						<FaqAnswer>{item.answer}</FaqAnswer>
					</FaqItemBlock>
				))}
			</FaqGrid>
		</Section>
	);
}

/** Render one comparison cell: a toned check chip, or emphasized/plain text. Mirrors the dialog. */
function GlanceValue({cell, tone, includedLabel}: {cell: GlanceCell; tone: LaneTone; includedLabel: string}) {
	if ("check" in cell) {
		return <CheckChip $tone={tone} aria-label={includedLabel}>✓</CheckChip>;
	}
	return cell.strong ? <b>{cell.text}</b> : <>{cell.text}</>;
}

/** The crawlable /features page body. */
export function FeaturesContent() {
	const {features} = useMessages();
	const {page, laneNames, encryption} = features;
	const lanes = useHowItWorksLanes();
	const glanceRows = useGlanceRows();
	return (
		<ContentSurface $wide>
			<Title>{page.title}</Title>
			<Lede>{page.lede}</Lede>

			<Grid>
				{lanes.map((lane) => (
					<LaneCard key={lane.id} $tone={lane.tone}>
						<BadgeRow>
							<Badge $tone={lane.tone}>{lane.badge}</Badge>
							<GhostTag>{lane.ghost}</GhostTag>
						</BadgeRow>
						<LaneTitle>{lane.title}</LaneTitle>
						<LaneDescription>{lane.description}</LaneDescription>
						<BulletList>
							{lane.bullets.map((bullet) => (
								<li key={bullet}>
									<Check $tone={lane.tone} aria-hidden="true">✓</Check>
									{bullet}
								</li>
							))}
						</BulletList>
						<LaneFoot>
							<span>{page.bestFor}</span>
							<b>{lane.bestFor}</b>
						</LaneFoot>
					</LaneCard>
				))}
			</Grid>

			<GlanceHeading>{page.glanceHeading}</GlanceHeading>
			<GlanceSub>{page.glanceSub}</GlanceSub>
			<GlanceTableWrap>
				<GlanceTable>
					<thead>
						<tr>
							<th aria-hidden="true"/>
							<th scope="col"><GlanceLane $tone="blue">{laneNames.p2p}</GlanceLane></th>
							<th scope="col"><GlanceLane $tone="teal">{laneNames.hosted}</GlanceLane></th>
						</tr>
					</thead>
					<tbody>
						{glanceRows.map((row) => (
							<tr key={row.label}>
								<th scope="row">{row.label}</th>
								<td><GlanceValue cell={row.p2p} tone="blue" includedLabel={page.included}/></td>
								<td><GlanceValue cell={row.hosted} tone="teal" includedLabel={page.included}/></td>
							</tr>
						))}
					</tbody>
				</GlanceTable>
			</GlanceTableWrap>

			<Section aria-labelledby="features-encryption-heading">
				<SectionTitle id="features-encryption-heading">{encryption.title}</SectionTitle>
				<SectionBody>{encryption.body}</SectionBody>
			</Section>

			<FeaturesFaq headingId="features-faq-heading"/>

			<Closing>
				{page.closing.before}<RouterLink to="/">{page.closing.startTransfer}</RouterLink>
				{page.closing.mid}<RouterLink to="/pricing">{page.closing.plansLimits}</RouterLink>
				{page.closing.after}
			</Closing>
		</ContentSurface>
	);
}
